import {
  InkController,
  InkBuilder,
  PathPointContext,
  InkCanvas2D,
  StrokeRenderer2D,
  StrokeRendererGL,
  BlendMode,
  Color,
  Rect,
  InkCodec,
  InkModel,
  Stroke,
  BrushGL
// @ts-ignore
} from 'digital-ink';

// @ts-ignore
import repository from './DataRepository';

type OffscreenLayer2D = any;

export class BasicInkController extends InkController {
  #context = new PathPointContext();
  canvas: InkCanvas2D;
  strokesLayer: OffscreenLayer2D;
  strokeRenderer: StrokeRenderer2D; // | StrokeRendererGL;
  builder: InkBuilder;
  color: Color;
  actions: { export: () => Promise<any>; };
  bounds: Rect | null;
  codec: any;
  inkModel: InkModel;
  tool: any;
  requested: boolean;

  constructor(inkCanvas: InkCanvas2D) {
    super();

    const StrokeRenderer =
      inkCanvas instanceof InkCanvas2D ? StrokeRenderer2D : StrokeRendererGL;

    this.canvas = inkCanvas;
    this.strokesLayer = inkCanvas.createLayer();

    this.strokeRenderer = new StrokeRenderer(this.canvas) as StrokeRenderer2D;

    this.builder = new InkBuilder();
    this.builder.onComplete = this.draw.bind(this);

    this.color = new Color(204, 204, 204);

    this.actions = {
      export: this.export.bind(this),
    };

    this.bounds = null;

    this.inkModel = new InkModel();
    this.codec = new InkCodec();

    this.requested = false;
  }

  init() {}

  registerInputProvider(pointerID: any, isPrimary: any) {
    if (Array.isArray(pointerID)) {
      // multi-touch should handle all changedTouches and to assign builders for each other
      if (isNaN(this.builder.pointerID))
        this.builder.pointerID = pointerID[0];
    } else {
      if (isPrimary) this.builder.pointerID = pointerID;
    }
  }

  getInkBuilder(pointerID: any) {
    if (Array.isArray(pointerID)) {
      if (pointerID.length > 0 && !pointerID.includes(this.builder.pointerID))
        return undefined;
      return this.builder;
    } else
      return this.builder.pointerID == pointerID ? this.builder : undefined;
  }

  reset(sensorPoint: any) {
    let ibo = this.buildInkBuilderOptions(sensorPoint);
    let sro = this.buildStrokeRendererOptions();

    this.builder.configure(ibo);
    this.strokeRenderer.configure(sro);
  }

  buildInkBuilderOptions(sample: any) {
    let tool = repository.get(this.tool);
    let brush = repository.get(tool.brush);

    this.#context.reset(sample, brush, this.color, tool.dynamics, tool.statics);

    return {
      brush,
      layout: this.#context.layout,
      pathPointCalculator: this.#context.calculate.bind(this.#context),
      pathPointProps: this.#context.statics,
    };
  }

  buildStrokeRendererOptions() {
    let tool = repository.get(this.tool);

    return {
      brush: repository.get(tool.brush),
      color: this.#context.color || this.color,
      blendMode: tool.blendMode || BlendMode.SOURCE_OVER,
    };
  }

  begin(sensorPoint: any) {
    this.reset(sensorPoint);

    this.builder.add(sensorPoint);
    this.builder.build();
  }

  move(sensorPoint: any) {
    this.builder.add(sensorPoint);

    if (!this.requested) {
      this.requested = true;

      this.builder.build();

      requestAnimationFrame(() => (this.requested = false));
    }

    // this.updateSensorHistory(sensorPoint);
  }

  end(sensorPoint: any) {
    this.builder.add(sensorPoint);
    this.builder.build();

    const b = this.strokeRenderer.strokeBounds;

    if (this.bounds) {
      this.bounds = this.bounds.union(b);
    } else {
      this.bounds = b;
    }
  }

  draw(pathPart: any) {
    this.strokeRenderer.draw(
      pathPart.added,
      pathPart.phase === InkBuilder.Phase.END,
    );

    if (pathPart.phase === InkBuilder.Phase.UPDATE) {
      this.strokeRenderer.drawPreliminary(pathPart.predicted);

      let dirtyArea = this.canvas.bounds.intersect(
        this.strokeRenderer.updatedArea,
      );

      if (dirtyArea) this.present(dirtyArea, pathPart.phase);
    } else if (pathPart.phase === InkBuilder.Phase.END) {
      let affectedArea = this.strokeRenderer.strokeBounds.union(
        this.strokeRenderer.updatedArea,
      );
      let dirtyArea = this.canvas.bounds.intersect(affectedArea);

      if (dirtyArea) this.present(dirtyArea, pathPart.phase);
    }

    if (pathPart.phase === InkBuilder.Phase.END) {
			const stroke = (this.strokeRenderer as StrokeRenderer2D).toStroke(this.builder);
			this.inkModel.addPath(stroke);
		}
  }

  present(dirtyArea: Rect, phase: any) {
    if (phase === InkBuilder.Phase.END)
      this.strokeRenderer.blendStroke(this.strokesLayer);

    this.canvas.clear(dirtyArea);
    this.canvas.blend(this.strokesLayer, { rect: dirtyArea });

    if (phase === InkBuilder.Phase.UPDATE)
      this.strokeRenderer.blendUpdatedArea();
  } 

  abort() {
    if (!this.builder.phase) return;

    const dirtyArea = this.strokeRenderer.affectedArea;

    this.strokeRenderer.abort();
    this.builder.abort();

    if (dirtyArea) this.refresh(dirtyArea);
  }

  refresh(dirtyArea = this.canvas.bounds) {
    this.canvas.clear(dirtyArea);
    // this.canvas.ctx.fillRect(dirtyArea.x, dirtyArea.y, dirtyArea.width, dirtyArea.height);
    this.canvas.blend(this.strokesLayer, { rect: dirtyArea });
  }

  clear() {
    this.strokesLayer.clear();
    this.canvas.clear();
    this.inkModel.clear();
  }

  async export() {
    const b = this.bounds || this.canvas.bounds;
    return await this.strokesLayer.toBlob(b, 'image/jpeg');
    // return await this.strokesLayer.toBlob(this.bounds, 'image/jpeg');
    // return await this.strokesLayer.toBlob(this.bounds, 'image/jpeg');
    // return this.canvas.ctx.getImageData(this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height);
  }

  async readCanvas() {
    const b = this.bounds || this.canvas.bounds;
    const blob = await this.strokesLayer.toBlob(b, 'image/jpeg');
    // const blob = await this.strokesLayer.toBlob(this.bounds, 'image/jpeg');
    const array = await blob.arrayBuffer();
    return array.toBase64();
    // const inverted = new Uint8Array(array).map((v) => Math.max(0, Math.min(255 - v, 255)));
    // return inverted.buffer.toBase64();
  }

  async exportUIM() {
    const codec = new InkCodec();
    const buffer = await codec.encodeInkModel(this.inkModel);
    return buffer;
  }

  async pipeline(strokes: Stroke[]) {
		for (let stroke of strokes) {
			stroke.buildPath({});
    }
	}

  redraw() {
		let strokes = this.inkModel.strokes;

		this.strokesLayer.clear();
		this.strokeRenderer.blendStrokes(strokes, this.strokesLayer);
		this.refresh();
	}

  async importUIM(buffer: ArrayBuffer) {
		this.clear();

		let inkModel = await this.codec.decodeInkModel(buffer);

		if (inkModel.brushes.find((brush: any) => brush instanceof BrushGL)) {
			alert("Current sample targets vector content only, raster content found");
			return;
		}

		await this.pipeline(inkModel.strokes);
		this.inkModel = inkModel;
		this.redraw();
	}

  static renderActionBar() {
    return `
			<action-bar>
				<file-opener type="uim" data-action="importUIM" data-trigger="buffer">
					<a href="javascript:void(0)">Open (.uim)</a>
				</file-opener>

				<file-saver type="uim" file-name="ink.uim" data-action="exportUIM" data-trigger="content">
					<a href="javascript:void(0)">Save (.uim)</a>
				</file-saver>
			</action-bar>
		`;
  }
}

